import { Card } from "@/components/ui/card";
import type { UsageGroup } from "../lib/aggregate";
import { formatTokens, formatUsd } from "../lib/format";

interface Props {
  title: string;
  groups: UsageGroup[];
  totalCost: number;
  emptyLabel?: string;
  labelFor?: (key: string) => string;
}

export function UsageBreakdownList({
  title,
  groups,
  totalCost,
  emptyLabel = "Sem dados no período",
  labelFor,
}: Props) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground tracking-tight">{title}</h3>
        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
          {groups.length} {groups.length === 1 ? "item" : "itens"}
        </span>
      </div>

      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">{emptyLabel}</p>
      ) : (
        <ul className="space-y-3">
          {groups.map((g) => {
            const pct = totalCost > 0 ? (g.costUsd / totalCost) * 100 : 0;
            const label = labelFor ? labelFor(g.key) : g.key;
            return (
              <li key={g.key}>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-sm text-foreground truncate">{label}</span>
                  <span
                    className="text-sm font-medium tabular-nums text-foreground"
                    style={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatUsd(g.costUsd)}
                  </span>
                </div>
                <div className="relative h-1.5 rounded-full bg-muted/50 overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full bg-primary/70"
                    style={{ width: `${Math.max(pct, totalCost > 0 ? 2 : 0)}%` }}
                  />
                </div>
                <div className="flex justify-between text-[11px] text-muted-foreground mt-1">
                  <span>{formatTokens(g.totalTokens)} tokens · {g.callCount} calls</span>
                  <span>{pct.toFixed(1)}%</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
